import type { FC, MouseEvent, ReactElement } from 'react'
import { cloneElement } from 'react'

import { useUnit } from 'effector-react'
import semver from 'semver/preload'

import { useToast } from '@/components/ui/toast.tsx'
import { $currentCreator } from '@/models/creators.ts'
import { useDesktopApp } from '@/modules/desktop/DesktopAppProvider'
import { sendStartSession } from '@/modules/desktop/utils'
import {
  sendToPusherChannel,
  usePusher,
} from '@/modules/pusher/PusherProvider.tsx'

import { Tooltip } from './tooltip'

interface Props {
  href: string
  children: ReactElement<JSX.IntrinsicElements['a']>
  tooltip?: string
}

export const CreatorBrowserLink: FC<Props> = ({ children, href, tooltip }) => {
  const { startedSessions, isApp, appVersion, sendCustomAppAction } =
    useDesktopApp()
  const activeCreator = useUnit($currentCreator)
  const toast = useToast()
  const isLinkClickable =
    activeCreator && isApp && appVersion && !semver.lt(appVersion, '1.1.1')

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isLinkClickable) {
      return
    }

    const activeSession = [...startedSessions.values()].find(
      (session) => session.creatorId === activeCreator.id,
    )

    event.preventDefault()

    if (semver.lt(appVersion, '2.0.0')) {
      if (activeSession?.pusherChannelName) {
        toast.success('Opened link in CreatorBrowser!')
        sendToPusherChannel(
          activeSession.pusherChannelName,
          'client-open-link',
          {
            href,
          },
        )
      } else {
        toast.success('Starting CreatorBrowser with link...')
        sendStartSession(activeCreator, href)
      }

      return
    }

    sendCustomAppAction('open-creator-link', {
      creatorId: activeCreator?.id,
      href,
    })
  }

  const aElement = cloneElement(children, { href, onClick: handleClick })

  if (!isLinkClickable) {
    return <Tooltip content="Only available in desktop app">{aElement}</Tooltip>
  }

  if (tooltip) {
    return <Tooltip content={tooltip}>{aElement}</Tooltip>
  }

  return aElement
}
